import { Box, Stack, Typography, useMediaQuery, Link } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { useState, useContext, useEffect } from "react"
import { NumericFormat } from "react-number-format"
import { ChatIconCTA, FC, SpecialTag, SvgIcon, TripData } from ".."
import { SiteMetadataContext } from "../../local/wrappers/SiteMetadataProvider"
import StickyTripBannerWrapper from "./StickyTripBannerWrapper"
import StickyTripBannerMobile from "./StickyTripBannerMobile"
import { get } from "lodash"
import TripCTA from "./TripCTA"

type StickyTripBannerProps = {
  tripTitle: string
  duration: string
  price: number
  maxPrice: number
  prefix: string
  bookDate: string
  savings: string
  addedValue: boolean
  specialOfferShortDescription: string
  onClick: () => void
  trip?: TripData | null
}

const StickyTripBanner: FC<StickyTripBannerProps> = ({
  tripTitle,
  duration,
  price,
  maxPrice,
  prefix,
  bookDate,
  savings,
  addedValue,
  specialOfferShortDescription,
  trip,
  onClick,
}) => {
  const isPriceAvailable = price && price > 200
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))

  // showing max price if available
  const getThreshold = (rawPrice: number) => {
    if (rawPrice <= 5000) return 0.1
    if (rawPrice <= 10000) return 0.08
    if (rawPrice <= 15000) return 0.07
    return 0.06
  }
  const shouldShowRange = (rawPrice: number, rawMaxPrice: number) => {
    const threshold = getThreshold(rawPrice)
    const difference = rawMaxPrice - rawPrice
    const percentDiff = difference / rawPrice
    return percentDiff >= threshold
  }
  const showRange = shouldShowRange(price, maxPrice)

  const siteMetadata = useContext(SiteMetadataContext)
  const [openInquiryModal, setOpenInquiryModal] = useState(false)
  const [openTripModal, setOpenTripModal] = useState(false)

  // Add padding-bottom to body to prevent content from being hidden behind sticky banner
  useEffect(() => {
    const originalPaddingBottom = document.body.style.paddingBottom
    document.body.style.paddingBottom = "9rem"
    
    return () => {
      document.body.style.paddingBottom = originalPaddingBottom
    }
  }, [])

  if (isMobile) {
    return (
      <StickyTripBannerMobile
        duration={duration}
        price={price}
        prefix={prefix}
        savings={savings}
        onClick={onClick}
      />
    )
  }

  return (
    <>
      <StickyTripBannerWrapper>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: '100%', flexWrap: { xs: 'wrap', md: 'wrap', lg: 'nowrap' } }}>
          {/* Left column: Trip name, price, duration */}
          <Stack direction="column" spacing={1} sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="body.md"
              sx={(theme) => ({
                color: theme.palette["neutral.100"].main,
                fontWeight: 600,
                fontSize: {
                  xs: "18px",
                },
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              })}
            >
              {tripTitle}
            </Typography>
            <Box display="flex" alignItems="center" gap={4}>
              {savings && (
                <SpecialTag
                  bookDate={bookDate}
                  savings={savings}
                  addedValue={addedValue}
                  specialOfferShortDescription={specialOfferShortDescription}
                  sx={{
                    position: "relative",
                    top: "unset",
                    left: "unset",
                    width: "fit-content",
                  }}
                />
              )}
              {duration && (
                <Typography
                  variant="body.md"
                  fontWeight={600}
                  textTransform="uppercase"
                  sx={(theme) => ({ color: theme.palette["neutral.100"].main })}
                >
                  {duration}
                </Typography>
              )}
              {isPriceAvailable ? (
                <Box
                  sx={(theme) => ({
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    color: theme.palette["neutral.100"].main,
                  })}
                >
                  <span
                    style={{
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "140%",
                    }}
                  >
                    {showRange ? (
                      <>
                        <NumericFormat
                          value={price}
                          displayType="text"
                          thousandSeparator
                          prefix={prefix}
                          decimalScale={0}
                        />
                        {" - "}
                        <NumericFormat
                          value={maxPrice}
                          displayType="text"
                          thousandSeparator
                          prefix={prefix}
                          decimalScale={0}
                        />
                      </>
                    ) : (
                      <NumericFormat
                        value={price}
                        displayType="text"
                        thousandSeparator
                        prefix={prefix}
                        decimalScale={0}
                      />
                    )}
                  </span>
                  <span
                    style={{
                      fontStyle: "normal",
                      fontWeight: 500,
                      lineHeight: "140%",
                      textTransform: "uppercase",
                    }}
                  >
                    / p.p.
                  </span>
                </Box>
              ) : (
                <Typography
                  variant="body.md"
                  sx={{
                    fontSize: "14px!important",
                    fontStyle: "normal",
                    fontWeight: 600,
                    lineHeight: "140%",
                    color: theme.palette["neutral.100"].main,
                  }}
                >
                  Please Inquire
                </Typography>
              )}
            </Box>
          </Stack>

          {/* Right column: Actions */}
          <Stack direction={{ xs: 'column', md: 'row' }} alignItems="center" gap={4} width={{ xs: '100%', md: 'auto' }} mt={{ xs: 2, md: 0 }}>
            <TripCTA onClick={onClick} />
            {/* Phone button */}
            <Box
              component="span"
              sx={(theme) => ({
                ...theme.typography["link.md"],
                backgroundColor: theme.palette["primary.800"].main,
                borderRadius: 1,
                lineHeight: 1,
                transition: theme.transitions.create(["background-color", "border-color", "color"]),
                mr: {
                  xs: "16px",
                  md: 0
                },
                "&:hover": {
                  backgroundColor: theme.palette["primary.900"].main,
                },
              })}
            >
              <Link
                href={siteMetadata.phoneUrl}
                variant="link.sm"
                display="flex"
                alignItems="center"
                minHeight="50px"
                gap="8px"
                color={(theme) => theme.palette["neutral.100"].main}
                underline="none"
                sx={{
                  textDecoration: "none",
                  padding: {
                    xs: theme.spacing(2.5, 4.6),
                    xl: theme.spacing(3, 5),
                  },
                  fontSize: {
                    xs: "14px",
                    xl: "18px",
                  },
                }}
              >
                <SvgIcon
                  viewBox="0 0 23 24"
                  variant="phone"
                  strokeMode
                  sx={(theme) => ({
                    transition: theme.transitions.create(["transform"]),
                    color: theme.palette["neutral.100"].main,
                    width: "18px",
                    height: "25px",
                    "& path": {
                      strokeWidth: 2.5,
                    },
                  })}
                />
                {siteMetadata.phone}
              </Link>
            </Box>
            {/* chat icon button */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: {
                  xs: 2,
                  xl: 2.5,
                },
                color: theme.palette["neutral.100"].main,
                fontWeight: 600,
                lineHeight: "140%",

                "#chat-button": {
                  position: {
                    xs: "absolute",
                    md: "static",
                  },
                  bottom: {
                    xs: "100px",
                    sm: "95px",
                  },
                  width: 62,
                  height: 62,
                  boxShadow: "none",
                  display: "flex !important",
                  opacity: 1,
                  transform: "none !important",
                  transition: "none !important",
                },

                "#chat-button > div": {
                  alignItems: "center",
                },

                "#chat-button svg": {
                  width: 22,
                  height: 22,
                },

                "#chat-button p": {
                  lineHeight: 1.5,
                },
              }}
            >
              <ChatIconCTA />
            </Box>
          </Stack>
        </Box>
      </StickyTripBannerWrapper>
    </>
  )
}

export default StickyTripBanner
